// features/source-monitoring/store.ts
import { create } from "zustand";
import type { CategoryId } from "../types/source.types";

type Publication = {
  id: number;
  name: string;
  category: CategoryId;
  subcategory: string;
};

type SelectionState = {
  clearPublicationsByTab: any;
  clearPublicationsBySubcategory: (subcategory: string) => void;
  // selected publications across all tabs/subcategories
  selectedPublications: Publication[];
  togglePublication: (pub: Publication) => void;
  setManyPublications: (pubs: Publication[]) => void;
  clearPublications: (predicate?: (pub: Publication) => boolean) => void;

  // UI state
  expandedGroups: Record<string, boolean>; // subcategoryId -> expanded
  toggleExpanded: (subcatId: string) => void;
  setDefaultExpanded: (subcategoryIds: string[]) => void;

  // tabs
  activeTab: CategoryId;
  setActiveTab: (tab: CategoryId) => void;

  // search
  search: string;
  setSearch: (q: string) => void;

  // limit
  limit: number;
  setLimit: (limit: number) => void;

  // date range
  dateRange: string;
  setDateRange: (dateRange: string) => void;

  // track which subcategories have had their first page auto-selected
  initializedSubcategories: Set<string>;
  markSubcategoryInitialized: (subcatId: string) => void;
};

export const useSourceBasedStore = create<SelectionState>((set) => ({
  selectedPublications: [],
  togglePublication: (pub) =>
    set((s) => {
      const exists = s.selectedPublications.some((p) => p.id === pub.id);
      if (exists) {
        return {
          selectedPublications: s.selectedPublications.filter(
            (p) => p.id !== pub.id,
          ),
        };
      }
      return {
        selectedPublications: [...s.selectedPublications, pub],
      };
    }),
  setManyPublications: (pubs) =>
    set((s) => {
      const ids = new Set(s.selectedPublications.map((p) => p.id));
      const merged = [
        ...s.selectedPublications,
        ...pubs.filter((p) => !ids.has(p.id)),
      ];
      return { selectedPublications: merged };
    }),
  clearPublications: (predicate) =>
    set((s) => {
      if (!predicate) return { selectedPublications: [] };
      return {
        selectedPublications: s.selectedPublications.filter(
          (p) => !predicate(p),
        ),
      };
    }),

  clearPublicationsByTab: (tab: CategoryId) =>
    set((s) => ({
      selectedPublications: s.selectedPublications.filter(
        (p) => p.category !== tab,
      ),
    })),
  clearPublicationsBySubcategory: (subcategory: string) =>
    set((s) => ({
      selectedPublications: s.selectedPublications.filter(
        (p) => p.subcategory !== subcategory,
      ),
    })),

  expandedGroups: {},
  toggleExpanded: (subcatId) =>
    set((s) => ({
      expandedGroups: {
        ...s.expandedGroups,
        [subcatId]: !s.expandedGroups[subcatId],
      },
    })),
  setDefaultExpanded: (subcategoryIds) =>
    set((s) => {
      const expanded = { ...s.expandedGroups };
      subcategoryIds.forEach((id) => {
        if (!(id in expanded)) {
          expanded[id] = true; // Set to expanded by default only if not already set
        }
      });
      return { expandedGroups: expanded };
    }),

  activeTab: "print",
  setActiveTab: (tab) => set({ activeTab: tab }),

  search: "",
  setSearch: (q) => set({ search: q }),

  limit: 10,
  setLimit: (limit) => set({ limit }),

  dateRange: "last_24_hours",
  setDateRange: (dateRange) => set({ dateRange }),

  initializedSubcategories: new Set(),
  markSubcategoryInitialized: (subcatId) =>
    set((s) => {
      const newSet = new Set(s.initializedSubcategories);
      newSet.add(subcatId);
      return { initializedSubcategories: newSet };
    }),
}));
